import { useCallback, useMemo } from "react";
import { EntitySelector } from "@/components/EntitySelector";

/** A custom field option wrapped in the entity shape EntitySelector expects. */
interface OptionEntity {
  id: string;
}

/** Props for the MultiSelectOptionsInput component. */
interface MultiSelectOptionsInputProps {
  options: string[];
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
  disabled?: boolean;
  size?: "default" | "sm";
}

/**
 * Multi-select input for multi-select custom fields, rendered as a searchable
 * multi-entity selector with chips for the selected options.
 */
export function MultiSelectOptionsInput({
  options,
  selectedValues,
  onValuesChange,
  disabled = false,
  size = "default",
}: MultiSelectOptionsInputProps) {
  const entities = useMemo(() => {
    // Include already-selected values not present in the schema options so
    // stale selections stay visible instead of silently disappearing
    const allOptions = [...new Set([...options, ...selectedValues])];
    return new Map<string, OptionEntity>(
      allOptions.map((option) => [option, { id: option }]),
    );
  }, [options, selectedValues]);

  const fetchEntities = useCallback(
    async (search?: string) => {
      if (!search) {
        return options;
      }
      const searchLower = search.toLowerCase();
      return options.filter((option) =>
        option.toLowerCase().includes(searchLower),
      );
    },
    [options],
  );

  return (
    <EntitySelector<OptionEntity>
      mode="multi"
      entities={entities}
      listLoading={false}
      fetchEntities={fetchEntities}
      getDisplayName={(option) => option.id}
      selectedIds={selectedValues}
      onSelectedIdsChange={onValuesChange}
      placeholder="Select options"
      searchPlaceholder="Search options..."
      entityLabel="options"
      disabled={disabled}
      size={size}
    />
  );
}
